/**
 * Token Storage Service (Infrastructure)
 *
 * Secure storage and management of authentication tokens.
 * Refresh tokens are stored as HTTP-only cookies by the backend.
 */

import type { AuthTokens } from '../../domain/types';

// ============================================================================
// Storage Keys
// ============================================================================

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'auth_token',
  TOKEN_EXPIRY: 'auth_token_expiry',
  USER_DATA: 'auth_user',
} as const;

// ============================================================================
// Token Storage Interface
// ============================================================================

export interface TokenStorage {
  /**
   * Store authentication tokens
   */
  setTokens(tokens: AuthTokens): void;

  /**
   * Get current access token
   */
  getAccessToken(): string | null;

  /**
   * Check if access token is expired
   */
  isTokenExpired(): boolean;

  /**
   * Get token expiry timestamp
   */
  getTokenExpiry(): number | null;

  /**
   * Clear all stored tokens
   */
  clearTokens(): void;

  /**
   * Store user data for offline access
   */
  setUserData(userData: unknown): void;

  /**
   * Get stored user data
   */
  getUserData<T>(): T | null;

  /**
   * Clear user data
   */
  clearUserData(): void;

  /**
   * Clear all auth-related storage
   */
  clearAll(): void;
}

// ============================================================================
// Token Storage Implementation
// ============================================================================

export class TokenStorageImpl implements TokenStorage {
  setTokens(tokens: AuthTokens): void {
    try {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);

      // Calculate and store expiry timestamp
      const expiryTime = Date.now() + tokens.expiresIn * 1000;
      localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());

      // Dispatch auth change event for providers
      window.dispatchEvent(new Event('auth-change'));
    } catch (error) {
      console.error('Failed to store tokens:', error);
    }
  }

  getAccessToken(): string | null {
    try {
      return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    } catch {
      return null;
    }
  }

  isTokenExpired(): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) return true;

    // Consider token expired 60 seconds before actual expiry (buffer)
    return Date.now() >= expiry - 60000;
  }

  getTokenExpiry(): number | null {
    try {
      const expiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
      return expiry ? parseInt(expiry, 10) : null;
    } catch {
      return null;
    }
  }

  clearTokens(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);

      // Clean up legacy key if present
      localStorage.removeItem('auth_refresh_token');

      // Dispatch auth change event
      window.dispatchEvent(new Event('auth-change'));
    } catch (error) {
      console.error('Failed to clear tokens:', error);
    }
  }

  setUserData(userData: unknown): void {
    try {
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    } catch (error) {
      console.error('Failed to store user data:', error);
    }
  }

  getUserData<T>(): T | null {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  clearUserData(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    } catch (error) {
      console.error('Failed to clear user data:', error);
    }
  }

  clearAll(): void {
    this.clearTokens();
    this.clearUserData();
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const tokenStorage: TokenStorage = new TokenStorageImpl();
